import { useEffect, useState } from 'react';
import { C } from '../lib/theme';
import { styles } from '../lib/styles';
import { fmtDateTime } from '../lib/util';
import { resolveActorName } from '../auth/actorNames';
import { useUIState } from '../state/contexts/UIStateContext';
import { listAudit, type GlobalAuditEntry } from '../api/cases';

// #311: the feed is no longer cases-only. Rows for intake submissions, TN
// change requests and `SYSTEM:*` events have no cases row, so the API returns
// `registrant: null` for them rather than inventing a placeholder. Say what the
// row IS instead of leaving the Case cell looking like a broken case — the same
// idiom as resolveActorName(), which falls back to the raw id and only then to
// 'unknown'. The id itself is already shown next to this label.
function caseKindLabel(caseId: string): string {
  if (caseId.startsWith('SUB-')) return 'submission';
  if (caseId.startsWith('TCR-')) return 'TN change request';
  if (caseId.startsWith('CCR-')) return 'company change request';
  if (caseId.startsWith('SYSTEM:')) return 'system';
  return 'no case';
}

export function Audit() {
  const { auditFilter, setAuditFilter } = useUIState();
  const [entries, setEntries] = useState<GlobalAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    listAudit(200).then(items => {
      if (!active) return;
      setEntries(items);
      setLoading(false);
    }).catch(err => {
      if (!active) return;
      setError(err instanceof Error ? err.message : String(err));
      setLoading(false);
    });
    return () => { active = false; };
  }, []);

  const detailText = (d: string | Record<string, unknown>): string =>
    typeof d === 'string' ? d : JSON.stringify(d);
  // Case-less rows have no registrant to match on; fall back to the kind label
  // so typing "submission" finds the intake rows the way a registrant name
  // finds a case's rows.
  const registrantHaystack = (a: GlobalAuditEntry): string =>
    (a.registrant ?? caseKindLabel(a.caseId)).toLowerCase();
  const filtered = auditFilter
    ? entries.filter(a =>
        a.caseId.includes(auditFilter)
        || registrantHaystack(a).includes(auditFilter.toLowerCase())
        || a.action.includes(auditFilter.toUpperCase())
        || detailText(a.detail).toLowerCase().includes(auditFilter.toLowerCase()))
    : entries;

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 16 }}>Global Audit Trail</h2>
      {loading && (
        <div style={{ ...styles.card, marginBottom: 16, fontSize: 13, color: C.textMuted }}>
          Loading audit trail…
        </div>
      )}
      {!loading && error && (
        <div style={{ ...styles.card, marginBottom: 16, fontSize: 13, color: C.danger }}>
          Failed to load audit trail: {error}
        </div>
      )}
      {!loading && !error && entries.length === 0 && (
        <div style={{ ...styles.card, marginBottom: 16, fontSize: 13, color: C.textMuted }}>
          No audit entries yet. New activity will appear here as cases are created and updated.
        </div>
      )}
      <input aria-label="Filter audit entries" style={{ ...styles.input, marginBottom: 16, maxWidth: 400 }} placeholder="Filter by case, registrant, action, or detail..." value={auditFilter} onChange={e => setAuditFilter(e.target.value)} />
      <div style={styles.card}>
        <table style={styles.table}>
          <thead><tr>
            <th style={styles.th}>Timestamp</th><th style={styles.th}>Case</th><th style={styles.th}>Actor</th><th style={styles.th}>Action</th><th style={styles.th}>Detail</th>
          </tr></thead>
          <tbody>
            {filtered.slice(0, 100).map((a, i) => (
              <tr key={a.id ?? i}>
                <td style={styles.td}><span style={{ fontSize: 11, fontFamily: 'monospace', color: C.textMuted }}>{fmtDateTime(a.timestamp)}</span></td>
                <td style={styles.td}>
                  <span style={{ color: C.goldText, fontWeight: 600 }}>{a.caseId}</span>{' '}
                  {a.registrant
                    ? <span style={{ color: C.textDim, fontSize: 11 }}>{a.registrant}</span>
                    : <span style={{ ...styles.tag, background: C.infoBg, color: C.info, fontSize: 11 }}>{caseKindLabel(a.caseId)}</span>}
                </td>
                <td style={styles.td}>{resolveActorName(a)}</td>
                <td style={styles.td}><span style={{ ...styles.tag, background: C.goldBg, color: C.goldText, fontWeight: 600 }}>{a.action}</span></td>
                <td style={styles.td}><span style={{ fontSize: 12 }}>{detailText(a.detail)}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
